import React, { useState, useEffect } from 'react';
import {
  useTaxBasedAmountConfig,
  useTaxAmountStatus,
  useTaxAmountHistory,
  useTaxAmountStatistics,
  useUpdateTaxConfig,
  useUpdateTaxCurrentAmounts,
} from '../hooks/useTaxBasedAmounts';

export const TaxBasedAmountPanel: React.FC = () => {
  // Hooks de datos de configuración de montos
  const { config, loading: configLoading, refetch: refetchConfig } = useTaxBasedAmountConfig();
  const { status, loading: statusLoading, refetch: refetchStatus } = useTaxAmountStatus();
  const { history, loading: historyLoading, refetch: refetchHistory } = useTaxAmountHistory(undefined, undefined, 15);
  const { statistics, refetch: refetchStats } = useTaxAmountStatistics();
  const { updateConfig, updating: updatingConfig } = useUpdateTaxConfig();
  const { updateCurrentAmounts, updating: updatingAmounts } = useUpdateTaxCurrentAmounts();

  // Estados para modales
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  // State for taxed products
  const [taxedTargetInput, setTaxedTargetInput] = useState<string>('');
  const [taxedAlertInput, setTaxedAlertInput] = useState<string>('');
  const [taxedCurrentInput, setTaxedCurrentInput] = useState<string>('');

  // State for exempt products
  const [exemptTargetInput, setExemptTargetInput] = useState<string>('');
  const [exemptAlertInput, setExemptAlertInput] = useState<string>('');
  const [exemptCurrentInput, setExemptCurrentInput] = useState<string>('');

  // Initialize form with config values
  useEffect(() => {
    if (config) {
      setTaxedTargetInput(config.taxedTargetAmount?.toString() || '');
      setTaxedAlertInput(config.taxedAlertAmount?.toString() || '');
      setExemptTargetInput(config.exemptTargetAmount?.toString() || '');
      setExemptAlertInput(config.exemptAlertAmount?.toString() || '');
    }
  }, [config]);

  // Initialize current amounts from status
  useEffect(() => {
    if (status) {
      setTaxedCurrentInput(status.taxedAmount?.toString() || '0');
      setExemptCurrentInput(status.exemptAmount?.toString() || '0');
    }
  }, [status]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatPercentage = (percentage: number) => {
    return `${percentage.toFixed(1)}%`;
  };

  const calculatePercentage = (current: number, target: number) => {
    if (target === 0) return 0;
    return Math.min((current / target) * 100, 100);
  };

  const handleUpdateConfig = async () => {
    const taxedTarget = parseFloat(taxedTargetInput);
    const taxedAlert = parseFloat(taxedAlertInput);
    const exemptTarget = parseFloat(exemptTargetInput);
    const exemptAlert = parseFloat(exemptAlertInput);

    if (isNaN(taxedTarget) || isNaN(taxedAlert) || isNaN(exemptTarget) || isNaN(exemptAlert)) {
      alert('Por favor ingrese valores numéricos válidos');
      return;
    }

    if (taxedAlert > taxedTarget) {
      alert('La alerta de productos gravados no puede ser mayor que el objetivo');
      return;
    }

    if (exemptAlert > exemptTarget) {
      alert('La alerta de productos excluidos no puede ser mayor que el objetivo');
      return;
    }

    const confirmMessage = `¿Actualizar configuración de montos por impuesto?\n\n` +
      `PRODUCTOS GRAVADOS (con impuesto):\n` +
      `- Objetivo: ${formatCurrency(taxedTarget)}\n` +
      `- Alerta: ${formatCurrency(taxedAlert)}\n\n` +
      `PRODUCTOS EXCLUIDOS (sin impuesto):\n` +
      `- Objetivo: ${formatCurrency(exemptTarget)}\n` +
      `- Alerta: ${formatCurrency(exemptAlert)}`;

    if (!confirm(confirmMessage)) return;

    try {
      await updateConfig({
        taxedTargetAmount: taxedTarget,
        taxedAlertAmount: taxedAlert,
        exemptTargetAmount: exemptTarget,
        exemptAlertAmount: exemptAlert,
      });
      alert('✓ Configuración actualizada correctamente');
      await handleRefreshAll();
    } catch (error) {
      alert('Error al actualizar la configuración');
      console.error(error);
    }
  };

  const handleUpdateCurrentAmounts = async () => {
    const taxedCurrent = parseFloat(taxedCurrentInput);
    const exemptCurrent = parseFloat(exemptCurrentInput);

    if (isNaN(taxedCurrent) || isNaN(exemptCurrent)) {
      alert('Por favor ingrese valores numéricos válidos');
      return;
    }

    const confirmMessage = `¿Actualizar montos actuales del día?\n\n` +
      `PRODUCTOS GRAVADOS: ${formatCurrency(taxedCurrent)}\n` +
      `PRODUCTOS EXCLUIDOS: ${formatCurrency(exemptCurrent)}`;

    if (!confirm(confirmMessage)) return;

    try {
      await updateCurrentAmounts(taxedCurrent, exemptCurrent);
      alert('✓ Montos actuales actualizados correctamente');
      await handleRefreshAll();
    } catch (error) {
      alert('Error al actualizar los montos actuales');
      console.error(error);
    }
  };

  const handleRefreshAll = async () => {
    await Promise.all([
      refetchConfig(),
      refetchStatus(),
      refetchHistory(),
      refetchStats(),
    ]);
  };

  // Export data to Excel (CSV format)
  const handleExportToExcel = async () => {
    try {
      const headers = [
        'Fecha',
        'Tipo',
        'Monto Actual',
        'Monto Objetivo',
        'Porcentaje',
        'Estado',
        'Meta Alcanzada'
      ];

      const historyData = history.map(record => [
        [
          formatDate(record.date),
          'Productos Gravados',
          record.taxedAmount,
          record.taxedTarget,
          `${calculatePercentage(record.taxedAmount, record.taxedTarget).toFixed(1)}%`,
          record.taxedReached ? 'Meta Alcanzada' : 'Pendiente',
          record.taxedReached ? 'Sí' : 'No'
        ],
        [
          formatDate(record.date),
          'Productos Excluidos',
          record.exemptAmount,
          record.exemptTarget,
          `${calculatePercentage(record.exemptAmount, record.exemptTarget).toFixed(1)}%`,
          record.exemptReached ? 'Meta Alcanzada' : 'Pendiente',
          record.exemptReached ? 'Sí' : 'No'
        ]
      ]).flat();

      const csvContent = [
        headers.join(','),
        ...historyData.map(row => row.join(','))
      ].join('\n');

      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', `historial_montos_impuestos_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      alert('✓ Historial exportado correctamente');
    } catch (error) {
      alert('Error al exportar el historial');
      console.error(error);
    }
  };

  const loading = configLoading || statusLoading;
  const updating = updatingConfig || updatingAmounts;

  if (loading) {
    return <div className="loading">Cargando configuración de impuestos...</div>;
  }

  return (
    <div className="tax-switch-control-panel">
      <div className="panel-header">
        <h2>Control por Tipo de Impuesto</h2>
        <p className="panel-description">
          Controla productos gravados y excluidos de forma independiente
        </p>
      </div>

      {/* Quick Access Navigation */}
      <div className="card quick-access-card">
        <h3>Accesos Rápidos</h3>
        <div className="quick-access-buttons">
          <button
            className="btn-quick-access"
            onClick={() => setShowHistoryModal(true)}
            title="Ver historial completo y estadísticas"
          >
            <span className="icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="20" x2="18" y2="10"/>
                <line x1="12" y1="20" x2="12" y2="4"/>
                <line x1="6" y1="20" x2="6" y2="14"/>
              </svg>
            </span>
            <span className="text">Ver Historial</span>
          </button>

          <button
            className="btn-quick-access"
            onClick={handleRefreshAll}
            title="Refrescar todos los datos"
          >
            <span className="icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="23 4 23 10 17 10"/>
                <polyline points="1 20 1 14 7 14"/>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
              </svg>
            </span>
            <span className="text">Actualizar Datos</span>
          </button>

          <button
            className="btn-quick-access"
            onClick={handleExportToExcel}
            title="Exportar historial a Excel"
          >
            <span className="icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
            </span>
            <span className="text">Exportar Historial</span>
          </button>
        </div>
      </div>

      {/* Taxed Products Control */}
      <div className="card tax-control-card taxed-products">
        <div className="tax-header">
          <h3>
            <span className="icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="1" x2="12" y2="23"/>
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
              </svg>
            </span>
            Productos Gravados (Con Impuesto)
          </h3>
          <p className="description">Productos que incluyen IVA u otros impuestos</p>
        </div>

        <div className="tax-status-grid">
          {/* Current Status */}
          <div className="status-card">
            <div className="status-label">Monto Actual del Día</div>
            <div className="status-value taxed">
              {formatCurrency(status?.taxedAmount || 0)}
            </div>
            <div className="progress-bar">
              <div
                className="progress-fill taxed"
                style={{
                  width: `${calculatePercentage(
                    status?.taxedAmount || 0,
                    config?.taxedTargetAmount || 1
                  )}%`
                }}
              />
            </div>
            <div className="progress-text">
              {calculatePercentage(
                status?.taxedAmount || 0,
                config?.taxedTargetAmount || 1
              ).toFixed(1)}% del objetivo
            </div>
          </div>

          {/* Target */}
          <div className="status-card">
            <div className="status-label">Objetivo</div>
            <div className="status-value">
              {formatCurrency(config?.taxedTargetAmount || 0)}
            </div>
            <div className={`status-badge ${status?.taxedReached ? 'reached' : 'pending'}`}>
              {status?.taxedReached ? '✓ Alcanzado' : '○ Pendiente'}
            </div>
          </div>

          {/* Alert */}
          <div className="status-card">
            <div className="status-label">Alerta en</div>
            <div className="status-value warning">
              {formatCurrency(config?.taxedAlertAmount || 0)}
            </div>
            <div className={`status-badge ${status?.taxedAlertTriggered ? 'alert' : 'ok'}`}>
              {status?.taxedAlertTriggered ? '⚠ Alerta' : '✓ Normal'}
            </div>
          </div>
        </div>

        {/* Configuration Form */}
        <div className="config-form">
          <h4>Configurar Montos</h4>
          <div className="form-grid">
            <div className="form-group">
              <label>Objetivo Diario</label>
              <input
                type="number"
                value={taxedTargetInput}
                onChange={(e) => setTaxedTargetInput(e.target.value)}
                placeholder="Ej: 1000000"
                className="input-amount"
              />
            </div>

            <div className="form-group">
              <label>Monto de Alerta</label>
              <input
                type="number"
                value={taxedAlertInput}
                onChange={(e) => setTaxedAlertInput(e.target.value)}
                placeholder="Ej: 800000"
                className="input-amount"
              />
            </div>

            <div className="form-group">
              <label>Actualizar Monto Actual</label>
              <input
                type="number"
                value={taxedCurrentInput}
                onChange={(e) => setTaxedCurrentInput(e.target.value)}
                placeholder="Ej: 500000"
                className="input-amount"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Exempt Products Control */}
      <div className="card tax-control-card exempt-products">
        <div className="tax-header">
          <h3>
            <span className="icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="9" cy="21" r="1"/>
                <circle cx="20" cy="21" r="1"/>
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
              </svg>
            </span>
            Productos Excluidos (Sin Impuesto)
          </h3>
          <p className="description">Productos exentos de IVA u otros impuestos</p>
        </div>

        <div className="tax-status-grid">
          {/* Current Status */}
          <div className="status-card">
            <div className="status-label">Monto Actual del Día</div>
            <div className="status-value exempt">
              {formatCurrency(status?.exemptAmount || 0)}
            </div>
            <div className="progress-bar">
              <div
                className="progress-fill exempt"
                style={{
                  width: `${calculatePercentage(
                    status?.exemptAmount || 0,
                    config?.exemptTargetAmount || 1
                  )}%`
                }}
              />
            </div>
            <div className="progress-text">
              {calculatePercentage(
                status?.exemptAmount || 0,
                config?.exemptTargetAmount || 1
              ).toFixed(1)}% del objetivo
            </div>
          </div>

          {/* Target */}
          <div className="status-card">
            <div className="status-label">Objetivo</div>
            <div className="status-value">
              {formatCurrency(config?.exemptTargetAmount || 0)}
            </div>
            <div className={`status-badge ${status?.exemptReached ? 'reached' : 'pending'}`}>
              {status?.exemptReached ? '✓ Alcanzado' : '○ Pendiente'}
            </div>
          </div>

          {/* Alert */}
          <div className="status-card">
            <div className="status-label">Alerta en</div>
            <div className="status-value warning">
              {formatCurrency(config?.exemptAlertAmount || 0)}
            </div>
            <div className={`status-badge ${status?.exemptAlertTriggered ? 'alert' : 'ok'}`}>
              {status?.exemptAlertTriggered ? '⚠ Alerta' : '✓ Normal'}
            </div>
          </div>
        </div>

        {/* Configuration Form */}
        <div className="config-form">
          <h4>Configurar Montos</h4>
          <div className="form-grid">
            <div className="form-group">
              <label>Objetivo Diario</label>
              <input
                type="number"
                value={exemptTargetInput}
                onChange={(e) => setExemptTargetInput(e.target.value)}
                placeholder="Ej: 800000"
                className="input-amount"
              />
            </div>

            <div className="form-group">
              <label>Monto de Alerta</label>
              <input
                type="number"
                value={exemptAlertInput}
                onChange={(e) => setExemptAlertInput(e.target.value)}
                placeholder="Ej: 600000"
                className="input-amount"
              />
            </div>

            <div className="form-group">
              <label>Actualizar Monto Actual</label>
              <input
                type="number"
                value={exemptCurrentInput}
                onChange={(e) => setExemptCurrentInput(e.target.value)}
                placeholder="Ej: 400000"
                className="input-amount"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="card actions-card">
        <h3>Acciones</h3>
        <div className="action-buttons">
          <button
            className="btn-action btn-primary"
            onClick={handleUpdateConfig}
            disabled={updating}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{marginRight: '6px', verticalAlign: 'middle'}}>
              <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
              <polyline points="17 21 17 13 7 13 7 21"/>
              <polyline points="7 3 7 8 15 8"/>
            </svg>
            {updating ? 'Guardando...' : 'Guardar Configuración'}
          </button>

          <button
            className="btn-action btn-secondary"
            onClick={handleUpdateCurrentAmounts}
            disabled={updating}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{marginRight: '6px', verticalAlign: 'middle'}}>
              <polyline points="23 4 23 10 17 10"/>
              <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
            </svg>
            {updating ? 'Actualizando...' : 'Actualizar Montos'}
          </button>

          <button
            className="btn-action btn-tertiary"
            onClick={handleRefreshAll}
            disabled={updating}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{marginRight: '6px', verticalAlign: 'middle'}}>
              <polyline points="1 4 1 10 7 10"/>
              <polyline points="23 20 23 14 17 14"/>
              <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"/>
            </svg>
            {updating ? 'Cargando...' : 'Refrescar'}
          </button>
        </div>
      </div>

      {/* Summary Card */}
      <div className="card summary-card">
        <h3>Resumen del Día</h3>
        <div className="summary-grid">
          <div className="summary-item">
            <div className="summary-label">Total Vendido (Gravados + Excluidos)</div>
            <div className="summary-value total">
              {formatCurrency((status?.taxedAmount || 0) + (status?.exemptAmount || 0))}
            </div>
          </div>

          <div className="summary-item">
            <div className="summary-label">Total Objetivo</div>
            <div className="summary-value">
              {formatCurrency((config?.taxedTargetAmount || 0) + (config?.exemptTargetAmount || 0))}
            </div>
          </div>

          <div className="summary-item">
            <div className="summary-label">Progreso General</div>
            <div className="summary-value">
              {calculatePercentage(
                (status?.taxedAmount || 0) + (status?.exemptAmount || 0),
                (config?.taxedTargetAmount || 0) + (config?.exemptTargetAmount || 0)
              ).toFixed(1)}%
            </div>
          </div>

          <div className="summary-item">
            <div className="summary-label">Estado</div>
            <div className={`summary-badge ${
              status?.taxedReached && status?.exemptReached ? 'all-reached' :
              status?.taxedReached || status?.exemptReached ? 'partial' :
              'pending'
            }`}>
              {status?.taxedReached && status?.exemptReached ? '✓ Ambos Alcanzados' :
               status?.taxedReached || status?.exemptReached ? '◐ Parcialmente Alcanzado' :
               '○ Ambos Pendientes'}
            </div>
          </div>
        </div>
      </div>

      {/* Estadísticas */}
      {statistics && (
        <div className="card statistics-card">
          <h3>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{marginRight: '8px', verticalAlign: 'middle'}}>
              <line x1="12" y1="20" x2="12" y2="10"/>
              <line x1="18" y1="20" x2="18" y2="4"/>
              <line x1="6" y1="20" x2="6" y2="16"/>
            </svg>
            Estadísticas Generales
          </h3>

          <div className="summary-grid">
            <div className="summary-item">
              <span className="summary-label">Días Registrados:</span>
              <span className="summary-value">{statistics.totalDays}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Promedio Gravados:</span>
              <span className="summary-value">{formatCurrency(statistics.taxedAverage)}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Promedio Excluidos:</span>
              <span className="summary-value">{formatCurrency(statistics.exemptAverage)}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Días con Meta Gravados:</span>
              <span className="summary-value">{statistics.taxedReachedCount} / {statistics.totalDays}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Días con Meta Excluidos:</span>
              <span className="summary-value">{statistics.exemptReachedCount} / {statistics.totalDays}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Días con Ambas Metas:</span>
              <span className="summary-value highlight">{statistics.bothReachedCount} / {statistics.totalDays}</span>
            </div>
          </div>
        </div>
      )}

      {/* Historial Reciente */}
      {history.length > 0 && (
        <div className="card history-card">
          <div className="history-header">
            <h3>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{marginRight: '8px', verticalAlign: 'middle'}}>
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                <line x1="16" y1="2" x2="16" y2="6"/>
                <line x1="8" y1="2" x2="8" y2="6"/>
                <line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
              Historial Reciente (últimos 15 días)
            </h3>
            <button className="btn-secondary btn-sm" onClick={() => setShowHistoryModal(true)}>
              Ver Completo
            </button>
          </div>

          <div className="table-container">
            <table className="history-table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Gravados</th>
                  <th>Meta Gravados</th>
                  <th>Estado</th>
                  <th>Excluidos</th>
                  <th>Meta Excluidos</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {history.map((record) => (
                  <tr key={record.id}>
                    <td className="date-cell">{formatDate(record.date)}</td>
                    <td className="amount">{formatCurrency(record.taxedAmount)}</td>
                    <td>{formatCurrency(record.taxedTarget)}</td>
                    <td>
                      <span className={`badge ${record.taxedReached ? 'badge-success' : 'badge-pending'}`}>
                        {record.taxedReached ? 'Alcanzado' : 'Pendiente'}
                      </span>
                    </td>
                    <td className="amount">{formatCurrency(record.exemptAmount)}</td>
                    <td>{formatCurrency(record.exemptTarget)}</td>
                    <td>
                      <span className={`badge ${record.exemptReached ? 'badge-success' : 'badge-pending'}`}>
                        {record.exemptReached ? 'Alcanzado' : 'Pendiente'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal: Historial Completo */}
      {showHistoryModal && (
        <div className="modal-overlay" onClick={() => setShowHistoryModal(false)}>
          <div className="modal history-modal" onClick={(e) => e.stopPropagation()}>
            <h3>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{marginRight: '8px', verticalAlign: 'middle'}}>
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                <line x1="16" y1="2" x2="16" y2="6"/>
                <line x1="8" y1="2" x2="8" y2="6"/>
                <line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
              Historial Completo de Montos
            </h3>

            {historyLoading ? (
              <div className="loading">Cargando historial...</div>
            ) : history.length === 0 ? (
              <div className="empty-state">No hay registros en el historial</div>
            ) : (
              <div className="table-container">
                <table className="history-table">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Gravados</th>
                      <th>Meta</th>
                      <th>%</th>
                      <th>Estado</th>
                      <th>Excluidos</th>
                      <th>Meta</th>
                      <th>%</th>
                      <th>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((record) => (
                      <tr key={record.id}>
                        <td className="date-cell">{formatDate(record.date)}</td>
                        <td className="amount">{formatCurrency(record.taxedAmount)}</td>
                        <td>{formatCurrency(record.taxedTarget)}</td>
                        <td>{formatPercentage(calculatePercentage(record.taxedAmount, record.taxedTarget))}</td>
                        <td>
                          <span className={`badge-mini ${record.taxedReached ? 'reached' : 'pending'}`}>
                            {record.taxedReached ? '●' : '○'}
                          </span>
                        </td>
                        <td className="amount">{formatCurrency(record.exemptAmount)}</td>
                        <td>{formatCurrency(record.exemptTarget)}</td>
                        <td>{formatPercentage(calculatePercentage(record.exemptAmount, record.exemptTarget))}</td>
                        <td>
                          <span className={`badge-mini ${record.exemptReached ? 'reached' : 'pending'}`}>
                            {record.exemptReached ? '●' : '○'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowHistoryModal(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Estilos */}
      <style>{`
        .tax-switch-control-panel {
          padding: 0;
        }

        .tax-switch-control-panel .panel-header {
          background: var(--bg-primary, #ffffff);
          border: 1px solid var(--border-color, #e2e8f0);
          border-left: 4px solid #3b82f6;
          border-radius: 8px;
          padding: 16px 20px;
          margin-bottom: 16px;
        }

        .tax-switch-control-panel .panel-header h2 {
          font-size: 1.1rem;
          font-weight: 600;
          color: var(--text-primary, #1e293b);
          margin: 0 0 4px 0;
        }

        .tax-switch-control-panel .panel-description {
          font-size: 0.8rem;
          color: var(--text-secondary, #64748b);
          margin: 0;
        }

        .tax-switch-control-panel .card {
          background: var(--bg-primary, #ffffff);
          border: 1px solid var(--border-color, #e2e8f0);
          border-radius: 8px;
          padding: 16px;
          margin-bottom: 16px;
        }

        .tax-switch-control-panel .card h3 {
          font-size: 0.85rem;
          font-weight: 600;
          color: var(--text-primary, #1e293b);
          margin: 0 0 12px 0;
          display: flex;
          align-items: center;
        }

        /* Quick Access Card */
        .quick-access-card {
          border-left: 4px solid #60a5fa !important;
        }

        .quick-access-buttons {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
        }

        @media (max-width: 768px) {
          .quick-access-buttons {
            grid-template-columns: 1fr;
          }
        }

        .btn-quick-access {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          padding: 16px 12px;
          background: var(--bg-secondary, #f8fafc);
          border: 1px solid var(--border-color, #e2e8f0);
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s;
          color: var(--text-primary, #1e293b);
        }

        .btn-quick-access:hover {
          background: #eff6ff;
          border-color: #3b82f6;
        }

        .btn-quick-access .icon {
          color: #3b82f6;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .btn-quick-access .text {
          font-size: 0.8rem;
          font-weight: 500;
        }

        /* Tax Control Cards */
        .tax-control-card {
          border-left: 4px solid #3b82f6 !important;
        }

        .tax-control-card.taxed-products {
          border-left-color: #2563eb !important;
        }

        .tax-control-card.exempt-products {
          border-left-color: #60a5fa !important;
        }

        .tax-header {
          margin-bottom: 16px;
        }

        .tax-header h3 {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .tax-header h3 .icon {
          color: #3b82f6;
          display: flex;
          align-items: center;
        }

        .tax-header .description {
          font-size: 0.8rem;
          color: var(--text-secondary, #64748b);
          margin: 4px 0 0 0;
        }

        .tax-status-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
          margin-bottom: 16px;
        }

        @media (max-width: 768px) {
          .tax-status-grid {
            grid-template-columns: 1fr;
          }
        }

        .status-card {
          background: var(--bg-secondary, #f8fafc);
          border: 1px solid var(--border-color, #e2e8f0);
          border-radius: 6px;
          padding: 12px;
        }

        .status-label {
          font-size: 0.7rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: var(--text-secondary, #64748b);
          margin-bottom: 6px;
        }

        .status-value {
          font-size: 1.25rem;
          font-weight: 700;
          color: var(--text-primary, #1e293b);
          margin-bottom: 8px;
        }

        .status-value.taxed {
          color: #2563eb;
        }

        .status-value.exempt {
          color: #3b82f6;
        }

        .status-value.warning {
          color: #60a5fa;
        }

        .progress-bar {
          height: 6px;
          background: var(--border-color, #e2e8f0);
          border-radius: 3px;
          overflow: hidden;
          margin-bottom: 6px;
        }

        .progress-fill {
          height: 100%;
          background: linear-gradient(90deg, #3b82f6 0%, #2563eb 100%);
          border-radius: 3px;
          transition: width 0.3s ease;
        }

        .progress-fill.taxed {
          background: linear-gradient(90deg, #2563eb 0%, #1d4ed8 100%);
        }

        .progress-fill.exempt {
          background: linear-gradient(90deg, #3b82f6 0%, #2563eb 100%);
        }

        .progress-text {
          font-size: 0.75rem;
          color: var(--text-secondary, #64748b);
        }

        .status-badge {
          display: inline-block;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 0.7rem;
          font-weight: 600;
        }

        .status-badge.reached {
          background: #dbeafe;
          color: #1e40af;
        }

        .status-badge.pending {
          background: var(--bg-secondary, #f1f5f9);
          color: var(--text-secondary, #64748b);
        }

        .status-badge.alert {
          background: #dcfce7;
          color: #166534;
        }

        .status-badge.ok {
          background: #dbeafe;
          color: #1e40af;
        }

        /* Config Form */
        .config-form {
          background: var(--bg-secondary, #f8fafc);
          border: 1px solid var(--border-color, #e2e8f0);
          border-radius: 6px;
          padding: 12px;
        }

        .config-form h4 {
          font-size: 0.8rem;
          font-weight: 600;
          color: var(--text-primary, #1e293b);
          margin: 0 0 12px 0;
        }

        .form-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
        }

        @media (max-width: 768px) {
          .form-grid {
            grid-template-columns: 1fr;
          }
        }

        .form-group label {
          display: block;
          font-size: 0.75rem;
          font-weight: 500;
          color: var(--text-secondary, #64748b);
          margin-bottom: 4px;
        }

        .input-amount {
          width: 100%;
          padding: 8px 10px;
          border: 1px solid var(--border-color, #e2e8f0);
          border-radius: 6px;
          font-size: 0.85rem;
          background: var(--bg-primary, #ffffff);
          color: var(--text-primary, #1e293b);
          transition: border-color 0.2s;
        }

        .input-amount:focus {
          outline: none;
          border-color: #3b82f6;
        }

        /* Actions Card */
        .actions-card {
          border-left: 4px solid #93c5fd !important;
        }

        .action-buttons {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
        }

        .btn-action {
          padding: 10px 16px;
          border-radius: 6px;
          font-size: 0.8rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          border: none;
        }

        .btn-action.btn-primary {
          background: #3b82f6;
          color: white;
        }

        .btn-action.btn-primary:hover {
          background: #2563eb;
        }

        .btn-action.btn-secondary {
          background: var(--bg-secondary, #f1f5f9);
          color: var(--text-primary, #1e293b);
          border: 1px solid var(--border-color, #e2e8f0);
        }

        .btn-action.btn-secondary:hover {
          background: var(--border-color, #e2e8f0);
        }

        .btn-action.btn-tertiary {
          background: transparent;
          color: #3b82f6;
          border: 1px solid #3b82f6;
        }

        .btn-action.btn-tertiary:hover {
          background: #eff6ff;
        }

        .btn-action:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        /* Summary Card */
        .summary-card {
          border-left: 4px solid #2563eb !important;
        }

        .summary-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 12px;
        }

        @media (max-width: 1024px) {
          .summary-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 640px) {
          .summary-grid {
            grid-template-columns: 1fr;
          }
        }

        .summary-item {
          background: var(--bg-secondary, #f8fafc);
          border: 1px solid var(--border-color, #e2e8f0);
          border-radius: 6px;
          padding: 12px;
        }

        .summary-label {
          font-size: 0.7rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: var(--text-secondary, #64748b);
          margin-bottom: 4px;
          display: block;
        }

        .summary-value {
          font-size: 1rem;
          font-weight: 700;
          color: var(--text-primary, #1e293b);
          display: block;
        }

        .summary-value.total {
          color: #2563eb;
        }

        .summary-value.highlight {
          color: #1d4ed8;
        }

        .summary-badge {
          display: inline-block;
          padding: 4px 10px;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .summary-badge.all-reached {
          background: #dbeafe;
          color: #1e40af;
        }

        .summary-badge.partial {
          background: #dcfce7;
          color: #166534;
        }

        .summary-badge.pending {
          background: var(--bg-secondary, #f1f5f9);
          color: var(--text-secondary, #64748b);
        }

        /* Statistics Card */
        .statistics-card {
          border-left: 4px solid #60a5fa !important;
        }

        /* History Card */
        .history-card {
          border-left: 4px solid #93c5fd !important;
        }

        .history-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }

        .history-header h3 {
          margin: 0;
        }

        .btn-sm {
          padding: 6px 12px;
          font-size: 0.75rem;
        }

        /* Table Styles */
        .table-container {
          overflow-x: auto;
        }

        .history-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.8rem;
        }

        .history-table th {
          text-align: left;
          padding: 8px 10px;
          background: var(--bg-secondary, #f8fafc);
          color: var(--text-secondary, #64748b);
          font-weight: 600;
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          border-bottom: 1px solid var(--border-color, #e2e8f0);
        }

        .history-table td {
          padding: 8px 10px;
          border-bottom: 1px solid var(--border-color, #e2e8f0);
          color: var(--text-primary, #1e293b);
        }

        .history-table tr:last-child td {
          border-bottom: none;
        }

        .history-table .date-cell {
          white-space: nowrap;
        }

        .history-table .amount {
          font-weight: 600;
          color: #2563eb;
        }

        .badge {
          display: inline-block;
          padding: 3px 8px;
          border-radius: 4px;
          font-size: 0.7rem;
          font-weight: 600;
        }

        .badge-success {
          background: #dbeafe;
          color: #1e40af;
        }

        .badge-pending {
          background: var(--bg-secondary, #f1f5f9);
          color: var(--text-secondary, #64748b);
        }

        .badge-mini {
          display: inline-block;
          font-size: 0.9rem;
          font-weight: bold;
        }

        .badge-mini.reached {
          color: #2563eb;
        }

        .badge-mini.pending {
          color: #94a3b8;
        }

        /* Modal Styles */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          padding: 20px;
        }

        .history-modal {
          max-width: 900px;
          max-height: 80vh;
          overflow-y: auto;
          background: var(--bg-primary, #ffffff);
          border-radius: 8px;
          padding: 20px;
          border: 1px solid var(--border-color, #e2e8f0);
        }

        .history-modal h3 {
          font-size: 1rem;
          font-weight: 600;
          color: var(--text-primary, #1e293b);
          margin: 0 0 16px 0;
          display: flex;
          align-items: center;
        }

        .modal-actions {
          display: flex;
          justify-content: flex-end;
          margin-top: 16px;
          padding-top: 16px;
          border-top: 1px solid var(--border-color, #e2e8f0);
        }

        .loading {
          text-align: center;
          padding: 40px;
          color: var(--text-secondary, #64748b);
          font-size: 0.9rem;
        }

        .empty-state {
          text-align: center;
          padding: 40px;
          color: var(--text-secondary, #64748b);
          font-size: 0.9rem;
        }
      `}</style>
    </div>
  );
};
